import { useState } from 'react';

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    const nameValue = formData.get('name') as string;
    const emailValue = formData.get('email') as string;
    const messageValue = formData.get('message') as string;

    if (!nameValue || !emailValue || !messageValue) {
      setFormStatus('error');
      setFormMessage('请填写所有必填字段');
      return;
    }

    if (messageValue.length > 500) {
      setFormStatus('error');
      setFormMessage('留言内容不能超过500个字符');
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch('https://readdy.ai/api/form/d9sp5ql8o1vn02ekujd0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
          联系我们
        </h2>
        <p className="text-sm text-foreground-500 text-center mb-12">
          有任何问题或合作意向？欢迎给我们留言
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Contact Info */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-full bg-[oklch(var(--primary-500)/0.1)] flex items-center justify-center mb-3">
                <i className="ri-mail-line text-[oklch(var(--primary-500))] text-lg"></i>
              </div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮件</h4>
              <p className="text-sm text-foreground-500">support@bananavideo.cn</p>
            </div>

            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-full bg-[oklch(var(--accent-500)/0.1)] flex items-center justify-center mb-3">
                <i className="ri-phone-line text-[oklch(var(--accent-500))] text-lg"></i>
              </div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h4>
              <p className="text-sm text-foreground-500">400-888-8888</p>
              <p className="text-xs text-foreground-400 mt-1">服务时间：每日 9:00 - 21:00</p>
            </div>

            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-full bg-[oklch(var(--secondary-500)/0.1)] flex items-center justify-center mb-3">
                <i className="ri-map-pin-line text-[oklch(var(--secondary-500))] text-lg"></i>
              </div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
              <p className="text-sm text-foreground-500">北京市朝阳区建国路88号</p>
              <p className="text-sm text-foreground-500">香蕉大厦A座15层</p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="p-6 rounded-xl bg-background-50 border border-background-200/70"
            >
              <div className="flex flex-col gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    placeholder="请输入您的姓名"
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 outline-none focus:border-[oklch(var(--primary-500))] transition-colors placeholder:text-foreground-400"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    placeholder="请输入您的邮箱地址"
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 outline-none focus:border-[oklch(var(--primary-500))] transition-colors placeholder:text-foreground-400"
                  />
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 outline-none focus:border-[oklch(var(--primary-500))] transition-colors cursor-pointer"
                  >
                    <option value="general">一般咨询</option>
                    <option value="cooperation">商务合作</option>
                    <option value="creator">创作者扶持</option>
                    <option value="feedback">意见反馈</option>
                    <option value="other">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="请输入您的留言内容（最多500字）"
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 outline-none focus:border-[oklch(var(--primary-500))] transition-colors resize-none placeholder:text-foreground-400"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                {/* Honeypot */}
                <div className="hp-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  className="whitespace-nowrap px-6 py-2.5 rounded-full bg-[oklch(var(--primary-500))] text-background-50 text-sm font-semibold hover:bg-[oklch(var(--primary-500)/0.85)] transition-colors cursor-pointer"
                >
                  发送留言
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-emerald-600 bg-emerald-50 px-4 py-2 rounded-lg">{formMessage}</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">{formMessage}</p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}